/*
 * <share-story> — client-side share-image generator for Irshaid · Editions.
 * Renders a "mini product page" card from the current product, in the
 * email-invitation palette, at two sizes:
 *   story  1080 x 1920  (9:16)
 *   post   1080 x 810   (4:3)
 *
 * The story comes in three designs so the feed never repeats itself:
 *   paper — the plate: full-bleed print on paper, caption set left beneath it
 *   wall  — the gallery: print hung on ink under a tungsten glow, label centred
 *   film  — the negative: print as one frame in a sprocketed 35mm strip
 *
 * Latin text  -> JetBrains Mono (mono).
 * Arabic text -> Thmanyah (theme asset).
 * Coordinates + breath line are pulled per-product from product.description,
 * so every share carries that print's own pin. No app, no server.
 *
 * Tokens (in sync with the email + irshaid-kit.css):
 *   paper #fdfcf8 · ink #131414 · red #c40001 · olive #8a7a62 · amber #c98a2e · line #e4ddcf
 */
(() => {
  const PAPER = '#fdfcf8', INK = '#131414', RED = '#c40001';
  const OLIVE = '#8a7a62', AMBER = '#c98a2e', LINE = '#e4ddcf';
  const MONO = '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';
  const ARABIC = '"Thmanyah", "EB Garamond", Georgia, serif';
  const AR_RE = /[؀-ۿݐ-ݿﭐ-﷿ﹰ-﻿]/;

  const FORMATS = { story: { w: 1080, h: 1920 }, post: { w: 1080, h: 810 } };

  /* Instagram's own chrome sits on top of every story: the avatar, handle,
   * timer bar and close button own roughly the TOP 190px, the reply bar and the
   * link sticker own the BOTTOM ~250px, and while composing, the tool rail
   * (Aa, stickers, music) owns a ~150px column on the RIGHT down to y≈700.
   * Nothing that carries information may sit in those zones — the old band at
   * y=0 lost its tagline to the rail. Centre-anchored text at y≈206 is safe:
   * IG's top chrome is left- and right-hugging, the middle is clear.
   * See cerebrum 2026-08-11 + buglog bug-028. */
  const STORY = {
    // IG's top chrome is left- and right-hugging (close button, tool rail), so
    // the centre runs clear almost to the edge — the card uses it.
    wordmarkY: 104,       // baseline of the small centred wordmark
    artTop: 128, artH: 568,        // the print's box — no crop, so it letterboxes
    // three mockups in a row UNDER the print — never on top of it
    tilesTop: 730, tilesH: 190, tileGap: 18, tilesM: 74,
    captionTop: 954,      // flowing caption block starts here
    captionBottom: 1352,  // …and must finish here
    chipsY: 1376, chipsH: 52,
    domainY: 1478,
    // the red box doubles as the drop target for Instagram's link sticker, so
    // it runs tall enough to sit a full-width link inside it, and its label is
    // pinned to the TOP so the sticker has the middle to itself
    // measured against a real IG compose screen: the caption field starts
    // around y≈1840, so the box can run to ~1720 and still sit clear of it
    btnTop: 1510, btnH: 210,   // ends at 1720 — the link sticker drops inside
  };

  const THEMES = {
    paper: {
      bg: PAPER, title: INK, body: '#33302a', muted: OLIVE, eyebrow: AMBER,
      rule: RED, coord: RED, domain: INK, wordmark: OLIVE,
      chipStroke: 'rgba(19,20,20,.4)', chipText: INK,
    },
    wall: {
      // red reads harsh on ink — the pin goes amber on every dark ground, same
      // as film. Red stays for the tick and the button only.
      bg: INK, title: '#f6f1e6', body: 'rgba(246,241,230,.72)', muted: '#a08f75', eyebrow: AMBER,
      rule: RED, coord: AMBER, domain: '#f6f1e6', wordmark: '#a08f75',
      chipStroke: 'rgba(246,241,230,.38)', chipText: '#f6f1e6',
    },
    film: {
      bg: '#0e0f0f', title: '#f2ede2', body: 'rgba(242,237,226,.62)', muted: AMBER, eyebrow: AMBER,
      rule: RED, coord: AMBER, domain: '#f2ede2', wordmark: AMBER,
      chipStroke: 'rgba(242,237,226,.34)', chipText: '#f2ede2',
    },
  };

  class ShareStory extends HTMLElement {
    connectedCallback() {
      const d = this.dataset;
      this.data = {
        title: d.title || '',
        url: d.url || '',
        domain: d.domain || 'shop.irshaid.work',
        wordmark: d.wordmark || 'IRSHAID · EDITIONS',
        tagline: d.tagline || 'an archive of home',
        eyebrow: d.eyebrow || 'Shot on film',
        cta: d.cta || 'View on website',
        spec: d.spec || 'Printed on Hahnemühle Photo Rag 308gsm · Free Shipping',
      };
      // The gift card isn't a print: it has no coordinates, no mockup rooms and
      // no sizes. Its copy is set in the theme editor instead of pulled out of
      // the description, and its chips are the three denominations.
      this.kind = d.kind === 'giftcard' ? 'giftcard' : 'print';
      if (this.kind === 'giftcard') {
        this.coord = '';
        this.breath = d.breath ||
          'choosing a print for someone else is hard. this hands the choosing back to them.';
        this.data.spec = d.detail ||
          'Any print in the shop · Arrives by email · Never expires';
      } else {
        this.parseDescription(d.desc || '');
      }
      try { this.chips = JSON.parse(d.chips || '[]'); } catch (e) { this.chips = []; }
      this.hero = d.hero || '';
      try { this.thumbs = JSON.parse(d.thumbs || '[]'); } catch (e) { this.thumbs = []; }
      this.heroId = d.heroId || '';
      try { this.media = (JSON.parse(d.media || '[]') || []).filter(Boolean); }
      catch (e) { this.media = []; }
      try { this.sizes = JSON.parse(d.sizes || '[]'); } catch (e) { this.sizes = []; }

      this.format = 'story';
      this.design = THEMES[d.design] ? d.design : 'paper';
      this.images = {};
      this.blob = null;

      this.modal = this.querySelector('[data-share-story-modal]');
      this.canvas = this.querySelector('[data-share-story-canvas]');
      this.spinner = this.querySelector('[data-share-story-spinner]');
      this.hint = this.querySelector('[data-share-story-hint]');
      this.shareBtn = this.querySelector('[data-share-story-share]');
      this.designRow = this.querySelector('[data-share-story-designs]');

      this.querySelector('[data-share-story-open]').addEventListener('click', () => this.open());
      this.querySelector('[data-share-story-close]').addEventListener('click', () => this.close());
      this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.close(); });
      this.querySelectorAll('[data-share-story-format]').forEach((t) =>
        t.addEventListener('click', () => this.setFormat(t.dataset.shareStoryFormat, t)));
      this.querySelectorAll('[data-share-story-design]').forEach((t) =>
        t.addEventListener('click', () => this.setDesign(t.dataset.shareStoryDesign, t)));
      this.querySelector('[data-share-story-download]').addEventListener('click', () => this.download());
      this.querySelector('[data-share-story-copy]').addEventListener('click', () => this.copyLink());
      if (this.shareBtn) this.shareBtn.addEventListener('click', () => this.shareImage());
    }

    // Pull the coordinate line and the opening sentence out of the description.
    // The card supplies its own fixed paper/shipping line, so we only need the
    // first evocative sentence after the coordinates as the "breath".
    parseDescription(desc) {
      const text = desc.replace(/📍/g, '').replace(/\s+/g, ' ').trim();
      // accept both straight ('") and curly (’‘ ”“) quotes / primes (′″) in the DMS
      const coord = text.match(/\d{1,3}\s*°\s*\d{1,2}\s*['′’‘]\s*[\d.]+\s*["″”“]\s*[NS][ ,]*\d{1,3}\s*°\s*\d{1,2}\s*['′’‘]\s*[\d.]+\s*["″”“]\s*[EW]/);
      // normalize to straight quotes so every card reads the same
      this.coord = coord ? coord[0].replace(/\s+/g, '').replace(/[’‘]/g, "'").replace(/[”“]/g, '"') : '';
      let rest = coord ? text.replace(coord[0], ' ') : text;
      rest = rest.replace(/https?:\/\/\S+/g, '').replace(/\s+/g, ' ').trim();
      const first = (rest.match(/[^.!?]+[.!?]/) || [rest])[0].trim();
      this.breath = first.slice(0, 150);
    }

    async open() {
      if (this.modal.showModal) this.modal.showModal(); else this.modal.setAttribute('open', '');
      if (!this._loaded) { await this.loadAssets(); this._loaded = true; }
      await this.loadMockups();
      this.render();
    }
    close() { if (this.modal.close) this.modal.close(); else this.modal.removeAttribute('open'); }

    setFormat(f, tab) {
      if (!FORMATS[f] || f === this.format) return;
      this.format = f;
      this.querySelectorAll('[data-share-story-format]').forEach((t) => t.classList.remove('is-active'));
      tab.classList.add('is-active');
      this.render();
    }

    setDesign(d, chip) {
      if (!THEMES[d] || d === this.design) return;
      this.design = d;
      this.querySelectorAll('[data-share-story-design]').forEach((t) => t.classList.remove('is-active'));
      chip.classList.add('is-active');
      this.render();
    }

    /* The share image carries TWO frames: the print itself (hero) and one
     * mockup. Which mockup depends on where the shopper is in the gallery —
     * whatever they swiped to is what they meant to show. Standing on the hero
     * with no swipe, we tuck in the first mockup so the card is never bare. */
    activeMediaId() {
      const scopes = [this.closest('.product'), document].filter(Boolean);
      for (const g of scopes) {
        const cur = g.querySelector('[data-target] button[aria-current="true"]');
        if (cur) return cur.closest('[data-target]').dataset.target;
        const act = g.querySelector('[data-media-id].is-active');
        if (act) return act.dataset.mediaId;
      }
      return '';
    }

    // media ids on the gallery are prefixed with the section id ("123-456").
    // The row takes up to three mockups, and whichever one the shopper swiped
    // to leads it — that's the one they meant to show.
    pickMockups() {
      const rest = (this.media || []).filter((m) => String(m.id) !== String(this.heroId));
      if (!rest.length) return [];
      const key = String(this.activeMediaId() || '').split('-').pop();
      const i = rest.findIndex((m) => String(m.id) === key);
      const ordered = i > 0 ? rest.slice(i).concat(rest.slice(0, i)) : rest;
      return ordered.slice(0, 3).map((m) => m.src);
    }

    async loadMockups() {
      const srcs = this.pickMockups();
      const key = srcs.join('|');
      if (key === this._mockKey) return;
      this._mockKey = key;
      this.spinner.hidden = false;
      const imgs = await Promise.all(srcs.map((src) => this.loadImage(src)));
      this.images.mockups = imgs.filter(Boolean);
      this.images.mockup = this.images.mockups[0] || null;
    }

    loadImage(src) {
      return new Promise((res) => {
        if (!src) return res(null);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => res(img);
        img.onerror = () => res(null);
        img.src = src;
      });
    }

    async loadAssets() {
      try {
        await Promise.all([
          document.fonts.load('500 40px "JetBrains Mono"'),
          document.fonts.load('400 40px "Thmanyah"'),
        ]);
        await document.fonts.ready;
      } catch (e) { /* fall back to stack */ }
      const [hero, t0, t1, t2] = await Promise.all([
        this.loadImage(this.hero),
        this.loadImage(this.thumbs[0]), this.loadImage(this.thumbs[1]), this.loadImage(this.thumbs[2]),
      ]);
      this.images = { hero, thumbs: [t0, t1, t2].filter(Boolean) };
    }

    render() {
      this.spinner.hidden = false;
      this.blob = null;
      if (this.shareBtn) this.shareBtn.hidden = true;

      const { w, h } = FORMATS[this.format];
      const T = THEMES[this.design] || THEMES.paper;
      const c = this.canvas; c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.fillStyle = T.bg; ctx.fillRect(0, 0, w, h);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';

      if (this.format === 'story') this.renderStory(ctx, w, h, T);
      else this.renderPost(ctx, w, h, T);

      this.spinner.hidden = true;
      c.toBlob((b) => {
        this.blob = b;
        if (this.shareBtn && this.canShareFile(b)) this.shareBtn.hidden = false;
      }, 'image/png');
    }

    /* ---------- STORY 1080 x 1920 ---------- */
    // Three designs, one skeleton: the print sits high and uncropped, the
    // caption flows in a measured band beneath it, and the footer (sizes,
    // domain, red button) is locked to the same Y in all three so the link
    // sticker always lands in the same place.
    renderStory(ctx, w, h, T) {
      if (this.kind === 'giftcard') this.storyGift(ctx, w, h, T);
      else if (this.design === 'wall') this.storyWall(ctx, w, h, T);
      else if (this.design === 'film') this.storyFilm(ctx, w, h, T);
      else this.storyPaper(ctx, w, h, T);
      this.storyFooter(ctx, w, T);
    }

    // 1 · PAPER — the plate. Print full-bleed on paper, caption set left.
    storyPaper(ctx, w, h, T) {
      const M = 74;
      this.wordmarkLine(ctx, w, T);
      this.fit(ctx, this.images.hero, 0, STORY.artTop, w, STORY.artH);
      this.mockupRow(ctx, w, T);
      this.caption(ctx, T, { x: M, width: w - M * 2, align: 'left' });
    }

    // 2 · WALL — the gallery. The print cut into a white mat and hung on ink
    // under a tungsten glow, with a centred museum label beneath.
    storyWall(ctx, w, h, T) {
      const M = 74;
      // mat margins follow the framer's convention: even at the top and sides,
      // deeper at the bottom, so the print doesn't look like it's sliding down
      const matTop = 28, matSide = 28, matBottom = 40;
      // the frame is sized to the art box, not the canvas, so the mockup band
      // below it always has its own room
      const fh = STORY.artH, ph = fh - matTop - matBottom;
      const pw = Math.round(ph * 1.5), fw = pw + matSide * 2;
      const fx = Math.round((w - fw) / 2), fy = STORY.artTop;

      this.glow(ctx, w, h, w / 2, fy + fh / 2, 780);
      this.wordmarkLine(ctx, w, T);

      // the frame, lifted off the wall by its own shadow
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,.55)'; ctx.shadowBlur = 44; ctx.shadowOffsetY = 16;
      ctx.fillStyle = PAPER; ctx.fillRect(fx, fy, fw, fh);
      ctx.restore();

      const px = fx + matSide, py = fy + matTop;
      this.fit(ctx, this.images.hero, px, py, pw, ph);

      this.mockupRow(ctx, w, T);
      this.caption(ctx, T, { x: M, width: w - M * 2, align: 'center' });
    }

    // 3 · FILM — the negative. One frame in a sprocketed strip, annotated in
    // the rebate the way an edge print reads.
    storyFilm(ctx, w, h, T) {
      // the strip is as wide as the frame it carries — a length of negative
      // laid on the light table, not a band ruled across the canvas
      const M = 74, pf = 34, imgH = STORY.artH - pf * 2;
      const top = STORY.artTop;
      const r = this.fit(ctx, this.images.hero, 0, top + pf, w, imgH);
      const sx = Math.round(r.x - 18), sw = Math.round(r.w + 36);
      this.perfRow(ctx, sx, top, sw, pf);
      this.perfRow(ctx, sx, top + pf + imgH, sw, pf);
      this.fit(ctx, this.images.hero, 0, top + pf, w, imgH);

      // the rebate annotation used to run under the strip — the mockup band
      // owns that space now, so the wordmark goes up top with the other two
      // designs and the eyebrow returns to the caption
      this.wordmarkLine(ctx, w, T);
      this.mockupRow(ctx, w, T);

      // the strip already carries the eyebrow + wordmark, so the caption drops
      // them and runs shorter
      this.caption(ctx, T, { x: M, width: w - M * 2, align: 'left' });
    }

    /* GIFT CARD — one skeleton in all three skins. The card artwork sits inset
     * on the ground as an object being shown (full-bleed would stack its own
     * ink band under the wordmark and read as one doubled header), and the
     * caption takes the room the mockup row would have owned. Same locked
     * footer as the prints, so the link sticker lands where it always does. */
    storyGift(ctx, w, h, T) {
      const M = 74, CW = w - M * 2;
      const cardW = CW, cardH = Math.round(cardW / 1.5);
      const cardY = STORY.artTop + 30;

      if (this.design === 'wall') this.glow(ctx, w, h, w / 2, cardY + cardH / 2, 760);
      this.wordmarkLine(ctx, w, T);

      if (this.design === 'film') {
        const pf = 30;
        this.perfRow(ctx, M - 18, cardY - pf, cardW + 36, pf);
        this.cover(ctx, this.images.hero, M, cardY, cardW, cardH);
        this.perfRow(ctx, M - 18, cardY + cardH, cardW + 36, pf);
      } else if (this.design === 'wall') {
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,.55)'; ctx.shadowBlur = 44; ctx.shadowOffsetY = 16;
        ctx.fillStyle = PAPER; ctx.fillRect(M - 24, cardY - 24, cardW + 48, cardH + 48);
        ctx.restore();
        this.cover(ctx, this.images.hero, M, cardY, cardW, cardH);
      } else {
        this.cover(ctx, this.images.hero, M, cardY, cardW, cardH);
        ctx.strokeStyle = LINE; ctx.lineWidth = 2;
        ctx.strokeRect(M + 1, cardY + 1, cardW - 2, cardH - 2);
      }

      this.caption(ctx, T, {
        x: M, width: CW,
        align: this.design === 'wall' ? 'center' : 'left',
        yTop: cardY + cardH + 130,
      });
    }

    // sizes · domain · red button — identical in all three designs
    storyFooter(ctx, w, T) {
      const M = 74;
      this.sizeChips(ctx, w / 2, STORY.chipsY, STORY.chipsH, 24, T);
      ctx.fillStyle = T.domain; ctx.font = `500 26px ${MONO}`;
      this.trackedCentered(ctx, this.data.domain.toUpperCase(), w / 2, STORY.domainY, 4);
      this.ctaButton(ctx, M, STORY.btnTop, w - M * 2, STORY.btnH);
    }

    wordmarkLine(ctx, w, T) {
      ctx.fillStyle = T.wordmark; ctx.font = `500 22px ${MONO}`;
      this.trackedCentered(ctx, this.data.wordmark.toUpperCase(), w / 2, STORY.wordmarkY, 5);
    }

    /* ---------- POST 1080 x 810 ---------- */
    // Landscape 4:3 feed post in the same three skins, so a story and its feed
    // post read as one pair. No red button — a feed post can't carry a link.
    renderPost(ctx, w, h, T) {
      const M = 64, CW = w - M * 2;
      const plate = this.design === 'paper';
      const bandH = plate ? 60 : 0;
      const stripH = 250;
      const heroTop = bandH, heroH = h - bandH - stripH;

      // the print takes the left two-thirds, the mockup the right third —
      // side by side, so the room never covers the photograph
      // the gift card is one 3:2 artwork — cropping it into the hero box would
      // eat its own type, so it's fitted whole, never cropped
      const mock = this.kind === 'giftcard' ? null : this.images.mockup;
      const gap = mock ? 8 : 0;
      const printW = mock ? Math.round(w * 0.66) : w;
      const mockX = printW + gap, mockW = w - mockX;

      if (plate) this.band(ctx, w, bandH);
      if (this.design === 'film' && this.kind !== 'giftcard') {
        this.perfRow(ctx, 0, heroTop, printW, 34);
        this.cover(ctx, this.images.hero, 0, heroTop + 34, printW, heroH - 68);
        this.perfRow(ctx, 0, heroTop + heroH - 34, printW, 34);
      } else if (this.kind === 'giftcard') {
        this.fit(ctx, this.images.hero, 0, heroTop + 12, printW, heroH - 24);
      } else {
        this.cover(ctx, this.images.hero, 0, heroTop, printW, heroH);
      }
      if (mock) this.cover(ctx, mock, mockX, heroTop, mockW, heroH);

      const sTop = h - stripH;
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';

      // eyebrow (left) — plus the wordmark on the right when there's no band
      ctx.fillStyle = T.eyebrow; ctx.font = `500 22px ${MONO}`;
      this.tracked(ctx, this.data.eyebrow.toUpperCase(), M, sTop + 44, 3);
      if (!plate) {
        ctx.fillStyle = T.muted; ctx.font = `500 20px ${MONO}`;
        const wm = this.data.wordmark.toUpperCase();
        this.tracked(ctx, wm, w - M - this.trackedWidth(ctx, wm, 2), sTop + 44, 2);
      }

      // title — always one line (shrinks to fit the strip width)
      const tf = this.titleFit(ctx, this.data.title, CW, 40, 38, 24);
      this.titleDraw(ctx, tf, M, sTop + 88, 0, 'left', T.title);

      // coordinates (left) + domain (right) on one baseline
      const rowY = sTop + 132;
      if (this.coord) this.coordLine(ctx, M, rowY, 24, 'left', T.coord, T.bg);
      ctx.fillStyle = T.domain; ctx.font = `500 22px ${MONO}`;
      this.tracked(ctx, this.data.domain.toUpperCase(),
        w - M - this.trackedWidth(ctx, this.data.domain.toUpperCase(), 2), rowY, 2);

      // size hint chips, centered
      this.sizeChips(ctx, w / 2, sTop + 172, 46, 22, T);
    }

    /* ---------- the flowing caption ---------- */
    // Every block is measured before anything is drawn, then the leftover room
    // is split evenly between them. A two-line title or a long breath line
    // tightens the gaps instead of pushing into the locked footer.
    caption(ctx, T, o) {
      const align = o.align || 'left';
      const yTop = o.yTop || STORY.captionTop;
      const yBottom = o.yBottom || STORY.captionBottom;
      const ax = align === 'center' ? o.x + o.width / 2 : o.x;
      const blocks = [];

      if (o.eyebrow !== false && this.data.eyebrow) {
        blocks.push({ h: 26, draw: (y) => {
          ctx.fillStyle = T.eyebrow; ctx.font = `500 26px ${MONO}`;
          this.line(ctx, this.data.eyebrow.toUpperCase(), ax, y + 26, 3, align);
        } });
      }

      const tf = this.titleFit(ctx, this.data.title, o.width, 48, 46, 36);
      // two-line height clears the Arabic descenders before the red tick
      blocks.push({ h: tf.one ? 52 : 118, draw: (y) =>
        this.titleDraw(ctx, tf, ax, y + 48, 58, align, T.title) });

      blocks.push({ h: 6, draw: (y) => {
        ctx.fillStyle = T.rule;
        ctx.fillRect(align === 'center' ? ax - 31 : ax, y, 62, 6);
      } });

      if (this.coord) {
        blocks.push({ h: 26, draw: (y) =>
          this.coordLine(ctx, ax, y + 26, 26, align, T.coord, T.bg) });
      }

      if (this.breath) {
        ctx.font = `400 27px ${MONO}`;
        const b = { breath: true, lines: this.lineCount(ctx, this.breath, o.width, 2, 0.3) };
        b.h = b.lines * 48 - 21;
        b.draw = (y) => {
          ctx.fillStyle = T.body; ctx.font = `400 27px ${MONO}`;
          this.wrap(ctx, this.breath, ax, y + 27, o.width, 48, b.lines, 0.3, align);
        };
        blocks.push(b);
      }

      if (o.spec !== false && this.data.spec) {
        blocks.push({ spec: true, h: 23, draw: (y) => {
          ctx.fillStyle = T.muted; ctx.font = `400 23px ${MONO}`;
          this.line(ctx, this.data.spec, ax, y + 23, 0.4, align);
        } });
      }

      // A long (two-line) title has to come out of something. The paper line
      // always stays (his call, 2026-10-30): the gaps tighten first, and only
      // if they'd drop below the floor does the breath go to one line.
      // The caption never runs past yBottom, so it can't reach the size chips
      // (it used to, on 8 prints). See buglog bug-share-caption-overflow.
      const MIN_GAP = 22, MAX_GAP = 64;
      const sum = () => blocks.reduce((a, b) => a + b.h, 0);
      const measure = () => (yBottom - yTop - sum()) / Math.max(1, blocks.length - 1);
      let gap = measure();
      const br = blocks.find((b) => b.breath);
      if (gap < MIN_GAP && br && br.lines > 1) {
        br.lines = 1; br.h = 27;
        gap = measure();
      }
      // never past yBottom: if it is still tight, the gaps give, not the footer
      gap = Math.min(MAX_GAP, gap < MIN_GAP ? Math.max(8, gap) : gap);

      let y = yTop;
      for (const b of blocks) { b.draw(y); y += b.h + gap; }
    }

    /* ---------- shared pieces ---------- */
    band(ctx, w, bh) {
      ctx.fillStyle = INK; ctx.fillRect(0, 0, w, bh);
      const cy = bh / 2 + 8;
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = '#f5f0e5'; ctx.font = `500 24px ${MONO}`; ctx.textAlign = 'left';
      this.tracked(ctx, this.data.wordmark.toUpperCase(), 40, cy, 2);
      ctx.fillStyle = OLIVE; ctx.font = `400 22px ${MONO}`;
      this.tracked(ctx, this.data.tagline.toUpperCase(), w - 40 - this.trackedWidth(ctx, this.data.tagline.toUpperCase(), 1.5), cy, 1.5);
    }

    // fill the box, cropping the overflow (used where a crop is harmless)
    cover(ctx, img, x, y, bw, bh, ay) {
      if (!img) { ctx.fillStyle = 'rgba(128,128,128,.12)'; ctx.fillRect(x, y, bw, bh); return; }
      const s = Math.max(bw / img.width, bh / img.height);
      const dw = img.width * s, dh = img.height * s;
      const dx = x + (bw - dw) / 2, dy = y + (bh - dh) * (ay == null ? 0.5 : ay);
      ctx.save(); ctx.beginPath(); ctx.rect(x, y, bw, bh); ctx.clip();
      ctx.drawImage(img, dx, dy, dw, dh); ctx.restore();
      return { x, y, w: bw, h: bh };
    }

    // fit the whole frame inside the box — the print is never cropped
    fit(ctx, img, x, y, bw, bh) {
      if (!img) {
        ctx.fillStyle = 'rgba(128,128,128,.12)'; ctx.fillRect(x, y, bw, bh);
        return { x, y, w: bw, h: bh };
      }
      const s = Math.min(bw / img.width, bh / img.height);
      const dw = img.width * s, dh = img.height * s;
      const dx = x + (bw - dw) / 2, dy = y + (bh - dh) / 2;
      ctx.drawImage(img, dx, dy, dw, dh);
      return { x: dx, y: dy, w: dw, h: dh };
    }

    // The mockups, three across, UNDER the print — never on top of it. The
    // print is the subject; these are the rooms it ends up in.
    mockupRow(ctx, w, T) {
      const imgs = (this.images.mockups || []).slice(0, 3);
      if (!imgs.length) return;
      const { tilesTop: y, tilesH: h, tileGap: g, tilesM: M } = STORY;
      const tw = Math.floor((w - M * 2 - g * 2) / 3);
      // fewer than three still sit on the same grid, centred
      const total = tw * imgs.length + g * (imgs.length - 1);
      let x = Math.round((w - total) / 2);
      imgs.forEach((img) => {
        // crop from the upper third — that's where a room shot hangs its art
        this.cover(ctx, img, x, y, tw, h, 0.34);
        ctx.strokeStyle = T.chipStroke; ctx.lineWidth = 2;
        ctx.strokeRect(x + 1, y + 1, tw - 2, h - 2);
        x += tw + g;
      });
    }

    // tungsten wash behind the hung print, same lamp as the viewing room
    glow(ctx, w, h, cx, cy, r) {
      const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      g.addColorStop(0, 'rgba(201,138,46,.15)');
      g.addColorStop(1, 'rgba(201,138,46,0)');
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
    }

    // 35mm sprocket holes, bled off both edges
    perfRow(ctx, x0, y, w, h) {
      ctx.save();
      ctx.beginPath(); ctx.rect(x0, y, w, h); ctx.clip();
      ctx.fillStyle = '#17181a'; ctx.fillRect(x0, y, w, h);
      const hw = 34, hh = Math.round(h * 0.52), hy = y + (h - hh) / 2;
      ctx.fillStyle = '#efe9dc';
      for (let x = x0 + 14; x < x0 + w - 14; x += 62) { this.roundRect(ctx, x, hy, hw, hh, 5); ctx.fill(); }
      ctx.restore();
    }

    coordLine(ctx, x, y, size, align, color, hole) {
      size = size || 26; color = color || RED; hole = hole || PAPER;
      const r = size * 0.34;
      ctx.font = `500 ${size}px ${MONO}`;
      const total = r * 2 + 12 + this.trackedWidth(ctx, this.coord, 1.2);
      const sx = align === 'center' ? x - total / 2 : x;
      // sit the pin inline with the text cap height, not below the baseline
      this.pin(ctx, sx + r, y - size * 0.42, r, color, hole);
      ctx.fillStyle = color; ctx.font = `500 ${size}px ${MONO}`; ctx.textAlign = 'left';
      this.tracked(ctx, this.coord, sx + r * 2 + 12, y, 1.2);
    }

    // compact vector map pin (teardrop + hole), drawn around vertical center cy
    pin(ctx, cx, cy, r, color, hole) {
      ctx.save(); ctx.fillStyle = color || RED; ctx.beginPath();
      ctx.arc(cx, cy, r, Math.PI, 0, false);
      ctx.lineTo(cx, cy + r * 1.7);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = hole || PAPER; ctx.beginPath();
      ctx.arc(cx, cy, r * 0.42, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }

    // Row of outlined size chips (a hint that sizes are available), centered on cx.
    sizeChips(ctx, cx, y, h, fs, T) {
      // normalize separators so 18x12 / 18X12 / 18 x 12 all read "18×12"
      const src = (this.chips && this.chips.length) ? this.chips : (this.sizes || []);
      const sizes = src.slice(0, 3).map((s) => s.replace(/(\d)\s*[xX×]\s*(\d)/g, '$1×$2'));
      if (!sizes.length) return;
      const padX = 20, gap = 16;
      ctx.font = `500 ${fs}px ${MONO}`;
      const widths = sizes.map((s) => Math.max(92, ctx.measureText(s).width + padX * 2));
      const total = widths.reduce((a, b) => a + b, 0) + gap * (sizes.length - 1);
      let x = cx - total / 2;
      const baseline = y + h / 2 + fs * 0.34;
      sizes.forEach((s, i) => {
        const cw = widths[i];
        ctx.strokeStyle = T.chipStroke; ctx.lineWidth = 2;
        this.roundRect(ctx, x, y, cw, h, 5); ctx.stroke();
        ctx.fillStyle = T.chipText; ctx.font = `500 ${fs}px ${MONO}`;
        this.trackedCentered(ctx, s, x + cw / 2, baseline, 0.5);
        x += cw + gap;
      });
      ctx.textAlign = 'left';
    }

    ctaButton(ctx, x, y, bw, bh) {
      ctx.fillStyle = RED; this.roundRect(ctx, x, y, bw, bh, 20); ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = `500 28px ${MONO}`;
      // label pinned to the top of the box — the box is the sticker's drop
      // target, so the middle and lower thirds are left empty for the link
      this.trackedCentered(ctx, this.data.cta.toUpperCase(), x + bw / 2, y + 44, 3);
      ctx.textAlign = 'left';
    }

    /* ---------- text helpers ---------- */
    tracked(ctx, text, x, y, tr) {
      const prev = ctx.textAlign; ctx.textAlign = 'left';
      let cx = x;
      for (const ch of text) { ctx.fillText(ch, cx, y); cx += ctx.measureText(ch).width + tr; }
      ctx.textAlign = prev;
      return cx - tr;
    }
    trackedWidth(ctx, text, tr) {
      let wsum = 0; for (const ch of text) wsum += ctx.measureText(ch).width + tr; return wsum - tr;
    }
    trackedCentered(ctx, text, cx, y, tr) {
      return this.tracked(ctx, text, cx - this.trackedWidth(ctx, text, tr) / 2, y, tr);
    }
    line(ctx, text, x, y, tr, align) {
      return align === 'center'
        ? this.trackedCentered(ctx, text, x, y, tr)
        : this.tracked(ctx, text, x, y, tr);
    }

    // split "English - Arabic" into its Latin and Arabic parts (any separator)
    splitTitle(text) {
      const m = text.match(AR_RE);
      if (!m) return { latin: text.trim(), arabic: '' };
      const i = text.indexOf(m[0]);
      return {
        latin: text.slice(0, i).replace(/[-–—·|/\\\s]+$/, '').trim(),
        arabic: text.slice(i).trim(),
      };
    }

    // Measure the title without drawing it: one line (English + Arabic) when it
    // fits at a readable size, otherwise English on line 1, Arabic on line 2.
    titleFit(ctx, text, maxW, latinBase, arabicBase, oneLineMin) {
      const { latin, arabic } = this.splitTitle(text);
      const oneLineWidth = (s) => {
        ctx.font = `600 ${s}px ${MONO}`;
        const lw = latin ? this.trackedWidth(ctx, latin.toUpperCase(), 1) : 0;
        let aw = 0;
        if (arabic) { ctx.font = `400 ${Math.round(s * 1.12)}px ${ARABIC}`; aw = ctx.measureText(arabic).width; }
        return lw + (latin && arabic ? s * 0.7 : 0) + aw;
      };
      let s = latinBase;
      while (s > oneLineMin && oneLineWidth(s) > maxW) s -= 2;
      if (oneLineWidth(s) <= maxW) return { one: true, latin, arabic, s, w: oneLineWidth(s) };

      let ls = latinBase;
      ctx.font = `600 ${ls}px ${MONO}`;
      while (ls > 24 && this.trackedWidth(ctx, latin.toUpperCase(), 1) > maxW) {
        ls -= 2; ctx.font = `600 ${ls}px ${MONO}`;
      }
      let as = arabicBase;
      if (arabic) {
        ctx.font = `400 ${as}px ${ARABIC}`;
        while (as > 20 && ctx.measureText(arabic).width > maxW) { as -= 2; ctx.font = `400 ${as}px ${ARABIC}`; }
      }
      return { one: false, latin, arabic, ls, as };
    }

    // Draw a measured title. x is the left edge, or the centre when centred.
    titleDraw(ctx, f, x, y1, lineGap, align, color) {
      ctx.fillStyle = color || INK; ctx.textAlign = 'left';
      if (f.one) {
        let cx = align === 'center' ? x - f.w / 2 : x;
        if (f.latin) { ctx.font = `600 ${f.s}px ${MONO}`; cx = this.tracked(ctx, f.latin.toUpperCase(), cx, y1, 1); }
        if (f.arabic) {
          cx += f.latin ? f.s * 0.7 : 0;
          ctx.font = `400 ${Math.round(f.s * 1.12)}px ${ARABIC}`;
          ctx.fillText(f.arabic, cx, y1);
        }
        return y1;
      }
      ctx.font = `600 ${f.ls}px ${MONO}`;
      this.line(ctx, f.latin.toUpperCase(), x, y1, 1, align);
      const y2 = y1 + lineGap;
      if (f.arabic) {
        ctx.font = `400 ${f.as}px ${ARABIC}`;
        const aw = ctx.measureText(f.arabic).width;
        ctx.fillText(f.arabic, align === 'center' ? x - aw / 2 : x, y2);
      }
      return y2;
    }

    // kept for callers that want measure + draw in one go
    titleBlock(ctx, text, x, y1, maxW, latinBase, arabicBase, lineGap, oneLineMin) {
      const f = this.titleFit(ctx, text, maxW, latinBase, arabicBase, oneLineMin || 36);
      return this.titleDraw(ctx, f, x, y1, lineGap, 'left', INK);
    }

    lineCount(ctx, text, maxW, maxLines, tr) {
      const words = text.split(' '); let line = '', n = 1;
      for (const wd of words) {
        const test = line ? line + ' ' + wd : wd;
        if (this.trackedWidth(ctx, test, tr) > maxW && line) { n++; line = wd; } else line = test;
      }
      return Math.min(n, maxLines);
    }

    wrap(ctx, text, x, y, maxW, lh, maxLines, tr, align) {
      const words = text.split(' '); let line = ''; const lines = [];
      for (const wd of words) {
        const test = line ? line + ' ' + wd : wd;
        if (this.trackedWidth(ctx, test, tr) > maxW && line) { lines.push(line); line = wd; }
        else line = test;
      }
      if (line) lines.push(line);
      if (lines.length > maxLines) { lines.length = maxLines; lines[maxLines - 1] += '…'; }
      lines.forEach((ln, i) => this.line(ctx, ln, x, y + i * lh, tr, align));
      this._wrapLines = lines.length;          // last-drawn line count
      // return the baseline just past the last line so the next block clears it
      return y + lines.length * lh;
    }

    roundRect(ctx, x, y, w, h, r) {
      r = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }

    /* ---------- output ---------- */
    canShareFile(blob) {
      if (!blob || !navigator.canShare) return false;
      try { return navigator.canShare({ files: [new File([blob], 'x.png', { type: 'image/png' })] }); }
      catch (e) { return false; }
    }
    filename() {
      const slug = (this.data.title || 'irshaid').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'print';
      return `irshaid-${slug}-${this.design}-${this.format}.png`;
    }
    async shareImage() {
      if (!this.blob) return;
      const file = new File([this.blob], this.filename(), { type: 'image/png' });
      try { await navigator.share({ files: [file], text: this.data.url }); } catch (e) {}
    }
    download() {
      if (!this.blob) { this.flash('Still composing — try again in a moment.'); return; }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(this.blob); a.download = this.filename(); a.click();
      URL.revokeObjectURL(a.href); this.flash('Saved — post it to your story or feed.');
    }
    async copyLink() {
      try { await navigator.clipboard.writeText(this.data.url); this.flash('Link copied.'); }
      catch (e) { this.flash(this.data.url); }
    }
    flash(m) { if (this.hint) this.hint.textContent = m; }
  }

  if (!customElements.get('share-story')) customElements.define('share-story', ShareStory);
})();
